import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { submitAdvertiserApplication } from '@/services/advertiserApplicationService';
import { cn } from '@/lib/utils';

const fieldClasses =
  'flex h-10 w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-mute focus:border-brand/40 focus:outline-none focus:ring-1 focus:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50';

const labelClasses = 'font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim';

function mapSignupError(code?: string) {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'That email already has an account. Sign in instead or use password reset.';
    case 'auth/invalid-email':
      return 'Enter a valid work email.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/network-request-failed':
      return 'Network error while creating your account. Try again.';
    default:
      return 'We could not create your advertiser account. Try again.';
  }
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { user, userData, advertiser, isAdvertiser, isSuperAdmin, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [contactName, setContactName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;

    if (isSuperAdmin) {
      navigate('/admin/advertisers', { replace: true });
      return;
    }

    if (isAdvertiser && advertiser?.status === 'active') {
      navigate('/', { replace: true });
      return;
    }

    if (isAdvertiser && advertiser?.status === 'paused') {
      navigate('/wallet', { replace: true });
      return;
    }

    if (isAdvertiser && advertiser?.status === 'pending') {
      navigate('/pending-approval', { replace: true });
    }
  }, [authLoading, user, isSuperAdmin, isAdvertiser, advertiser, navigate]);

  useEffect(() => {
    if (!user?.email) return;
    setEmail((current) => current || user.email || '');
  }, [user?.email]);

  useEffect(() => {
    if (!userData?.name) return;
    setContactName((current) => current || userData.name);
  }, [userData?.name]);

  const signedInWithoutAdvertiser = useMemo(
    () => Boolean(user && !isAdvertiser && !isSuperAdmin),
    [user, isAdvertiser, isSuperAdmin],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!contactName.trim() || !companyName.trim()) {
      setError('Contact name and company name are required.');
      return;
    }

    if (!signedInWithoutAdvertiser) {
      if (!email.trim()) {
        setError('Email is required.');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setSubmitting(true);
    try {
      if (!signedInWithoutAdvertiser) {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await credential.user.getIdToken(true);
      }

      await submitAdvertiserApplication({
        companyName: companyName.trim(),
        contactName: contactName.trim(),
        website: website.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
      });

      setSuccess('Application submitted. Redirecting to review status...');
      navigate('/pending-approval', { replace: true });
    } catch (err) {
      const fbError = err as { code?: string; message?: string };
      if (fbError.code?.startsWith('auth/')) {
        setError(mapSignupError(fbError.code));
      } else if (fbError.message) {
        setError(fbError.message);
      } else {
        setError('We could not submit your application. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg px-4 py-10 text-fg">
      <div className="mx-auto grid w-full max-w-5xl gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="overflow-hidden rounded-2xl border border-line bg-bg-elev px-6 py-8 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand font-serif-display text-2xl text-brand-ink">
              g
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-fg-mute">Grobot Ads Beta</div>
              <h1 className="mt-1 font-serif-display text-3xl leading-tight text-fg">
                Apply to advertise inside Grobot conversations
              </h1>
            </div>
          </div>

          <p className="mt-5 max-w-xl text-sm leading-6 text-fg-dim">
            Your products can appear as paid recommendations in Grobot chats across publisher embeds. This beta path
            creates your advertiser login immediately, then routes you into manual review before funding and product
            submission go live.
          </p>

          <div className="mt-8 space-y-3">
            {[
              'Create your advertiser login and submit company details',
              'We review and approve the account, assign your starting CPC, and email you',
              'You add funds, submit products, and we verify paid click reporting end to end',
            ].map((step, i) => (
              <div key={step} className="flex items-start gap-3 rounded-xl border border-line bg-bg-soft/40 p-4">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/15 font-mono text-[11px] font-semibold text-brand">
                  {i + 1}
                </div>
                <p className="text-sm text-fg">{step}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-start">
          <div className="w-full overflow-hidden rounded-2xl border border-line bg-bg-elev">
            <div className="border-b border-line px-6 py-5">
              <h2 className="font-serif-display text-2xl leading-none text-fg">
                {signedInWithoutAdvertiser ? 'Finish your application' : 'Create advertiser account'}
              </h2>
              <p className="mt-2 text-sm text-fg-dim">
                {signedInWithoutAdvertiser
                  ? 'You are signed in. Submit the company details below and we will route you into review.'
                  : 'Set your login credentials and submit the company details we need for review.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
              {error && (
                <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                  <div className="text-fg-dim">{error}</div>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-3 rounded-xl border border-pos/30 bg-pos/10 px-4 py-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-pos" />
                  <div className="text-fg-dim">{success}</div>
                </div>
              )}

              {!signedInWithoutAdvertiser && (
                <>
                  <SignupField id="email" label="Work email">
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="matt@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submitting}
                      required
                      className={fieldClasses}
                    />
                  </SignupField>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <SignupField id="password" label="Password">
                      <input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={submitting}
                        required
                        className={fieldClasses}
                      />
                    </SignupField>
                    <SignupField id="confirmPassword" label="Confirm password">
                      <input
                        id="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={submitting}
                        required
                        className={fieldClasses}
                      />
                    </SignupField>
                  </div>
                </>
              )}

              {signedInWithoutAdvertiser && (
                <div className="rounded-xl border border-line bg-bg-soft/40 px-4 py-3 text-sm text-fg-dim">
                  Signed in as <span className="font-medium text-fg">{email}</span>
                </div>
              )}

              <SignupField id="contactName" label="Your name">
                <input
                  id="contactName"
                  placeholder="Matt"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  disabled={submitting}
                  required
                  className={fieldClasses}
                />
              </SignupField>

              <SignupField id="companyName" label="Company">
                <input
                  id="companyName"
                  placeholder="Teraganix"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={submitting}
                  required
                  className={fieldClasses}
                />
              </SignupField>

              <div className="grid gap-4 sm:grid-cols-2">
                <SignupField id="website" label="Website">
                  <input
                    id="website"
                    placeholder="teraganix.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    disabled={submitting}
                    className={fieldClasses}
                  />
                </SignupField>
                <SignupField id="phone" label="Phone">
                  <input
                    id="phone"
                    placeholder="Optional"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={submitting}
                    className={fieldClasses}
                  />
                </SignupField>
              </div>

              <SignupField id="notes" label="What do you want to promote?">
                <textarea
                  id="notes"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                  placeholder="Product categories, current goals, launch timing, or anything we should know before approval."
                  className={cn(fieldClasses, 'h-auto min-h-[112px] py-2')}
                />
              </SignupField>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand text-sm font-medium text-brand-ink transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {signedInWithoutAdvertiser ? 'Submit application' : 'Create account and apply'}
              </button>
            </form>

            <div className="space-y-2 border-t border-line px-6 py-5 text-sm text-fg-mute">
              <p>
                Already have an advertiser login?{' '}
                <Link to="/login" className="font-medium text-brand underline underline-offset-2">
                  Sign in
                </Link>
                .
              </p>
              <p>
                Need help first? Email{' '}
                <a href="mailto:support@grobotix.ai" className="font-medium text-brand underline underline-offset-2">
                  support@grobotix.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SignupField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={labelClasses}>
        {label}
      </label>
      {children}
    </div>
  );
}
